import {Box} from "@mui/material";
import {FolioItem} from "crqlar-react-common/dist/models/billing/folioItem.model";
import {TaxRate} from "crqlar-react-common/dist/models/rate/taxRate.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {ServiceCategory} from "crqlar-react-common/dist/models/serviceCategory/serviceCategory.model";
import {addMinutes, diffInMinutes, formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {useTranslation} from "react-i18next";
import {FOLIO_COLUMN_GAP, FOLIO_LINE_COLUMNS, FOLIO_ROW_HEIGHT, FolioCellBox, FolioCellText} from "./FolioCellBox";
import {FolioIntegerInput, FolioTimeInput} from "./FolioCellInputs";
import {DraftLine} from "./folioDraft.util";
import {FolioFormat} from "./folioFormat";
import FolioLineMenu from "./FolioLineMenu";
import {FolioLineIssue, isHandSetPrice, isIncludedLine, isTipLine, lineAmounts, lineDatePrefix, lineIssues, lineTimeLabel} from "./folioLine.util";
import FolioPriceCell from "./FolioPriceCell";
import FolioServiceInfo from "./FolioServiceInfo";
import FolioServicePicker from "./FolioServicePicker";
import {folioArrivalSx} from "./folioStyles";
import FolioVatSelect from "./FolioVatSelect";
import {useLineRate} from "./lineRate";

export interface FolioLineRowProps {
    line: DraftLine
    service?: Service
    services: Service[]
    taxRates: TaxRate[]
    categories: ServiceCategory[]
    format: FolioFormat
    currency: string
    referenceDay?: Date
    /** The folio's lines can change. */
    editable: boolean
    /** The reservation's own line: fixed, but its VAT, discount and a missing price can be set. */
    primary: boolean
    vatRequired: boolean
    /** Errors show: a saved line, a line that was left, or Finalize was attempted. */
    showErrors: boolean
    autoFocus?: boolean
    /** It arrived from the server while the folio shows (the tip a signing posts): highlighted once. */
    arrived?: boolean
    onChange: (patch: Partial<FolioItem>) => void
    onPickService: (service: Service) => void
    onTouch: () => void
    onDiscount: () => void
    onMarketCode: () => void
    onDuplicate: () => void
    onRemove: () => void
}

const dateOf = (value?: Date) => (value ? new Date(value) : undefined);

/** A tip's time, minutes and VAT: it has none of its own. */
export const NOT_APPLICABLE = '—';

/**
 * One 40px line: every cell the same 28px box, read-only or not; issues are marks inside the cells.
 * The service reads from the left, every value from the right (see FOLIO_COLUMNS). A locked line
 * (not a draft, a guest link out, a tip, an included part) is plain text throughout.
 */
const FolioLineRow = (props: FolioLineRowProps) => {
    const {line: {item}, service, format, editable, primary, vatRequired, showErrors, onChange, onTouch} = props;
    const {t} = useTranslation();
    const tip = isTipLine(item);
    const included = isIncludedLine(item);
    const locked = !editable || tip || included;
    const fixed = locked || primary;

    const start = dateOf(item.timeSpanStart);
    const end = dateOf(item.timeSpanEnd);
    const minutes = start && end ? Math.max(0, Math.round(diffInMinutes(end, start))) : 0;
    const rate = useLineRate(service, start, !tip);
    const amounts = lineAmounts(item);
    const issues = lineIssues(item, {vatRequired});
    // A locked line can't be fixed any more: no red or amber outlines on it
    const flagged = (issue: FolioLineIssue) => !locked && showErrors && issues.includes(issue);
    const handSet = !locked && !primary && !rate.loading && isHandSetPrice(item, rate.ratePriceCents);
    // What was charged stays as stored; a draft shows what saving it will store
    const grossCents = locked && item.totalAmount?.amountCents != null ? item.totalAmount.amountCents : amounts.grossCents;
    const datePrefix = lineDatePrefix(start, props.referenceDay);
    // A tip carries the treatment's time span (it is dated with it), but has no time or duration of its
    // own: "—", like its VAT
    const timeText = tip ? NOT_APPLICABLE : lineTimeLabel(start, props.referenceDay);
    const minutesText = tip ? NOT_APPLICABLE : String(minutes);

    const info = !tip && (!!service || !!item.description) ? (
        <FolioServiceInfo
            line={item}
            service={service}
            categories={props.categories}
            rate={rate}
            amounts={amounts}
            format={format}
            handSet={handSet}
            missingVat={issues.includes('NO_VAT')}
        />
    ) : undefined;

    const changeStart = (next: Date) => onChange({
        timeSpanStart: next,
        timeSpanEnd: end && start ? new Date(end.getTime() + next.getTime() - start.getTime()) : end,
    });

    return (
        <Box
            data-arrived={props.arrived || undefined}
            sx={[
                {
                    display: 'grid',
                    gridTemplateColumns: FOLIO_LINE_COLUMNS,
                    columnGap: FOLIO_COLUMN_GAP,
                    alignItems: 'center',
                    height: FOLIO_ROW_HEIGHT,
                    borderBottom: 1,
                    borderColor: 'divider',
                },
                !!props.arrived && folioArrivalSx,
            ]}
        >
            {fixed ? (
                <FolioCellBox>
                    <FolioCellText value={item.description || '—'}/>
                    {info}
                </FolioCellBox>
            ) : (
                <FolioServicePicker
                    services={props.services}
                    line={item}
                    categories={props.categories}
                    format={format}
                    date={start}
                    error={flagged('NO_SERVICE')}
                    autoFocus={props.autoFocus}
                    trailing={info}
                    onPick={props.onPickService}
                    onBlur={onTouch}
                />
            )}

            <FolioCellBox editable={!fixed} align={'right'} title={!tip && datePrefix && start ? formatFullDMY(start) : undefined}>
                {fixed ? <FolioCellText value={timeText}/> : (
                    <>
                        {datePrefix && <FolioCellText value={datePrefix} muted/>}
                        <FolioTimeInput start={start} onChange={changeStart} onBlur={onTouch} ariaLabel={t('Time')}/>
                    </>
                )}
            </FolioCellBox>

            <FolioCellBox editable={!fixed} align={'right'}>
                {fixed ? <FolioCellText value={minutesText}/> : (
                    <FolioIntegerInput
                        value={minutes}
                        min={0}
                        onChange={next => onChange({timeSpanEnd: addMinutes(start ?? new Date(), next)})}
                        onBlur={onTouch}
                        ariaLabel={t('Duration (min)')}
                    />
                )}
            </FolioCellBox>

            <FolioCellBox editable={!fixed} align={'right'}>
                {fixed ? <FolioCellText value={String(amounts.quantity)}/> : (
                    <FolioIntegerInput
                        value={amounts.quantity}
                        min={1}
                        onChange={quantity => onChange({quantity})}
                        onBlur={onTouch}
                        ariaLabel={t('Quantity')}
                    />
                )}
            </FolioCellBox>

            <FolioPriceCell
                line={item}
                format={format}
                currency={props.currency}
                locked={locked}
                primary={primary}
                error={flagged('NO_PRICE')}
                onChange={onChange}
                onTouch={onTouch}
                onDiscount={props.onDiscount}
            />

            {locked ? (
                <FolioCellBox align={'right'}>
                    <FolioCellText value={format.vat(item.taxRateSnap) ?? NOT_APPLICABLE}/>
                </FolioCellBox>
            ) : (
                <FolioVatSelect
                    line={item}
                    taxRates={props.taxRates}
                    format={format}
                    warnMissing={flagged('NO_VAT')}
                    onChange={onChange}
                    onBlur={onTouch}
                    ariaLabel={t('VAT')}
                />
            )}

            {/* The amount alone, like the price: the header names the currency */}
            <FolioCellBox align={'right'} sx={{fontVariantNumeric: 'tabular-nums'}}>
                <FolioCellText value={included ? t('Included') : format.amount(grossCents)}/>
            </FolioCellBox>

            <Box sx={{display: 'flex', justifyContent: 'center'}}>
                {!locked && (
                    <FolioLineMenu
                        line={item}
                        primary={primary}
                        vatRequired={vatRequired}
                        onChange={onChange}
                        onDiscount={props.onDiscount}
                        onMarketCode={props.onMarketCode}
                        onDuplicate={props.onDuplicate}
                        onRemove={props.onRemove}
                    />
                )}
            </Box>
        </Box>
    );
};

export default FolioLineRow;
